export const BOSS_SEASON_ELEMENTS = ['작열', '수냉', '전격', '풍압', '철갑'];

function cleanText(value) {
  return String(value ?? '').trim();
}

function positiveNumberText(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? String(value) : '';
}

function bossKey(seasonId, bossNo) {
  return `${cleanText(seasonId)}:${Number(bossNo)}`;
}

export function normalizeBossSeasonAssignments(seasonRows = [], bossRows = [], phaseRows = []) {
  const phases = new Map(phaseRows.map((phase) => [
    `${bossKey(phase?.season_id, phase?.boss_no)}:${Number(phase?.phase_no)}`,
    phase,
  ]));
  const bossesBySeason = new Map();
  for (const boss of bossRows) {
    const rows = bossesBySeason.get(cleanText(boss?.season_id)) || [];
    rows.push({
      season_id: cleanText(boss?.season_id),
      boss_no: Number(boss?.boss_no),
      boss_master_id: cleanText(boss?.boss_master_id),
      boss_name: cleanText(boss?.boss_name),
      element: BOSS_SEASON_ELEMENTS.includes(boss?.element) ? boss.element : '',
      coefficient: positiveNumberText(boss?.coefficient) || '1',
      phase1_hp: positiveNumberText(phases.get(`${bossKey(boss?.season_id, boss?.boss_no)}:1`)?.max_hp_eok),
      phase2_hp: positiveNumberText(phases.get(`${bossKey(boss?.season_id, boss?.boss_no)}:2`)?.max_hp_eok),
      phase3_hp: positiveNumberText(phases.get(`${bossKey(boss?.season_id, boss?.boss_no)}:3`)?.max_hp_eok),
    });
    bossesBySeason.set(cleanText(boss?.season_id), rows);
  }

  return seasonRows.map((season) => ({
    id: cleanText(season?.id),
    season_no: Number(season?.season_no),
    status: ['draft', 'active', 'closed'].includes(season?.status) ? season.status : 'draft',
    bosses: (bossesBySeason.get(cleanText(season?.id)) || []).sort((left, right) => left.boss_no - right.boss_no),
  })).filter((season) => Number.isInteger(season.season_no) && season.season_no >= 35)
    .sort((left, right) => right.season_no - left.season_no);
}

function emptyBoss(bossNo) {
  return {
    boss_no: bossNo,
    boss_master_id: '',
    boss_name: '',
    element: BOSS_SEASON_ELEMENTS[bossNo - 1],
    coefficient: '1',
    phase1_hp: '',
    phase2_hp: '',
    phase3_hp: '',
  };
}

export function createBossSeasonAssignmentDraft(season = {}) {
  const byBossNo = new Map((Array.isArray(season?.bosses) ? season.bosses : [])
    .map((boss) => [Number(boss?.boss_no), boss]));
  return {
    id: cleanText(season?.id),
    season_no: season?.season_no ?? '',
    status: ['draft', 'active', 'closed'].includes(season?.status) ? season.status : 'draft',
    bosses: Array.from({ length: 5 }, (_, index) => {
      const fallback = emptyBoss(index + 1);
      return { ...fallback, ...(byBossNo.get(index + 1) || {}), boss_no: index + 1 };
    }),
  };
}

export function createNextBossSeasonDraft(seasons = [], baseSeason = null) {
  const seasonNo = Math.max(34, ...seasons.map((season) => Number(season.season_no) || 0)) + 1;
  const base = baseSeason || seasons.find((season) => season.status === 'active') || seasons[0];
  const draft = createBossSeasonAssignmentDraft(base);
  return { ...draft, id: '', season_no: seasonNo, status: 'draft', bosses: draft.bosses.map(({ season_id, ...boss }) => ({ ...boss })) };
}

export function buildManualBossMaster(name, seasonNo, id) {
  const canonicalName = cleanText(name);
  if (!canonicalName || canonicalName.length > 80) throw new Error('보스 이름을 1~80자로 입력해주세요.');
  const season = Number(seasonNo);
  if (!Number.isInteger(season) || season < 35 || season > 9999) throw new Error('시즌 번호를 확인해주세요.');
  return {
    id, icon_id: `manual-${id}`, boss_key: `manual-${id}`,
    boss_name: canonicalName, canonical_name: canonicalName,
    first_seen_season: season, last_seen_season: season, is_visible: true,
    is_verified: false, raw: { source: 'manual' },
  };
}

export function buildBossSeasonAssignmentPayload(draft = {}) {
  const seasonNo = Number(draft?.season_no);
  if (!Number.isInteger(seasonNo) || seasonNo < 35) throw new Error('35시즌 이후 차수만 설정할 수 있습니다.');
  const bosses = Array.isArray(draft?.bosses) ? draft.bosses : [];
  if (bosses.length !== 5) throw new Error('1번부터 5번까지 보스를 모두 설정해주세요.');

  const masterIds = bosses.map((boss) => cleanText(boss?.boss_master_id));
  if (masterIds.some((id) => !id)) throw new Error('모든 위치에 Boss Master를 선택해주세요.');
  if (new Set(masterIds).size !== 5) throw new Error('한 시즌에 같은 Boss Master를 중복 배치할 수 없습니다.');

  const elements = bosses.map((boss) => cleanText(boss?.element));
  // 어느 보스가 비었는지 알려준다. 5칸이 다 똑같이 생겨서 번호 없이는 찾을 수 없었다.
  const missingElementNos = bosses
    .filter((boss) => !BOSS_SEASON_ELEMENTS.includes(cleanText(boss?.element)))
    .map((boss) => `${Number(boss?.boss_no)}번`);
  if (missingElementNos.length) throw new Error(`${missingElementNos.join(', ')} 보스의 약점 속성을 선택해주세요.`);
  if (new Set(elements).size !== 5) throw new Error('한 시즌의 약점 속성은 서로 중복될 수 없습니다.');

  return {
    seasonNo,
    status: ['draft', 'active', 'closed'].includes(draft?.status) ? draft.status : 'draft',
    bosses: bosses.map((boss, index) => {
      const damage = [1, 2, 3].map((phaseNo) => Number(boss?.[`phase${phaseNo}_hp`]));
      if (damage.some((value) => !Number.isFinite(value) || value <= 0)) {
        throw new Error(`${index + 1}번 보스의 1~3단 체력을 확인해주세요.`);
      }
      const coefficient = Number(boss?.coefficient || 1);
      if (!Number.isFinite(coefficient) || coefficient <= 0) throw new Error(`${index + 1}번 보스의 보정값을 확인해주세요.`);
      return {
        boss_no: index + 1,
        boss_master_id: masterIds[index],
        element: elements[index],
        coefficient,
        phase1_hp: damage[0],
        phase2_hp: damage[1],
        phase3_hp: damage[2],
      };
    }),
  };
}
